/**
 * `ldr_cache` store — caches roles and departments so employees can select
 * a role/department while offline (API-4378).
 *
 * Unlike iOS (in-memory only, lost on app kill), the web terminal persists
 * this cache to IndexedDB since page refreshes are more likely than iOS app
 * kills. Hydrate-then-revalidate: read the cache first (works offline);
 * when online, refresh in the background and overwrite via `setLDR`.
 *
 * Both operations fail soft (log + resolve) — losing the LDR cache only
 * degrades the offline role/department selection UX (falls back to no
 * selection), it never loses a punch record.
 */

import { LDR_CACHE_STORE, getValue, setValue } from './offlineDb';
import type { ClockRole, ClockDepartment } from '@src/api/punchTypes';

export interface LDRCache {
    key: string; // `${companyId}:${locationId}`
    roles: ClockRole[];
    departments: ClockDepartment[];
    updatedAt: string; // ISO 8601 — when last refreshed
}

function cacheKey(companyId: number, locationId: number): string {
    return `${companyId}:${locationId}`;
}

export async function setLDR(
    companyId: number,
    locationId: number,
    roles: ClockRole[],
    departments: ClockDepartment[]
): Promise<void> {
    const key = cacheKey(companyId, locationId);
    const entry: LDRCache = {
        key,
        roles,
        departments,
        updatedAt: new Date().toISOString()
    };
    try {
        await setValue(LDR_CACHE_STORE, key, entry);
    } catch (err) {
        console.error('[ldrCache] Failed to persist LDR cache', err);
    }
}

export async function getLDR(
    companyId: number,
    locationId: number
): Promise<LDRCache | null> {
    try {
        const entry = await getValue<LDRCache>(
            LDR_CACHE_STORE,
            cacheKey(companyId, locationId)
        );
        return entry ?? null;
    } catch (err) {
        console.error('[ldrCache] Failed to read LDR cache', err);
        return null;
    }
}
